// Dashboard data constants — extracted from Dashboard.jsx (Phase 19.10 refactor)
// Pure data only. No state, no hooks.
import {
  FileSignature,
  UserPlus,
  Car,
  Package,
  Receipt,
  CalendarPlus,
  Calendar,
  Building2,
  FileText,
  Users,
  Bell,
  ClipboardCheck,
  TrendingUp,
  Clock,
  Zap,
  Plus,
  Mail,
  DollarSign,
  Wallet,
  BarChart3,
  BookOpen,
  Shield,
  PiggyBank,
  LayoutDashboard,
  Truck,
  AlertCircle,
} from "lucide-react";

export const DEFAULT_WIDGETS = {
  quickActions: { id: "quickActions", name: "Quick Actions", enabled: true, order: 0 },
  stats: { id: "stats", name: "Statistics", enabled: true, order: 1 },
  calendarWidget: { id: "calendarWidget", name: "Calendar", enabled: true, order: 2 },
  recentQuotes: { id: "recentQuotes", name: "Recent Quotes", enabled: true, order: 3 },
  upcomingJobs: { id: "upcomingJobs", name: "Upcoming Jobs", enabled: true, order: 4 },
  notifications: { id: "notifications", name: "Notifications", enabled: true, order: 5 },
  moduleShortcuts: { id: "moduleShortcuts", name: "Module Shortcuts", enabled: true, order: 6 },
  financialSnapshot: { id: "financialSnapshot", name: "Financial Snapshot", enabled: true, order: 7 },
  emailPreview: { id: "emailPreview", name: "Email Inbox Preview", enabled: true, order: 8 },
};

export const ACCENT_COLORS = [
  { name: "Emerald", value: "#10b981", class: "bg-emerald-500" },
  { name: "Blue", value: "#3b82f6", class: "bg-blue-500" },
  { name: "Violet", value: "#8b5cf6", class: "bg-violet-500" },
  { name: "Rose", value: "#f43f5e", class: "bg-rose-500" },
  { name: "Amber", value: "#f59e0b", class: "bg-amber-500" },
  { name: "Cyan", value: "#06b6d4", class: "bg-cyan-500" },
  { name: "Pink", value: "#ec4899", class: "bg-pink-500" },
  { name: "Indigo", value: "#6366f1", class: "bg-indigo-500" },
];

export const AVAILABLE_ICONS = {
  FileSignature: { icon: FileSignature, name: "Document/Quote" },
  UserPlus: { icon: UserPlus, name: "Add User/Client" },
  Car: { icon: Car, name: "Vehicle" },
  Package: { icon: Package, name: "Package/Equipment" },
  Receipt: { icon: Receipt, name: "Receipt/Invoice" },
  CalendarPlus: { icon: CalendarPlus, name: "Calendar Add" },
  Calendar: { icon: Calendar, name: "Calendar" },
  Building2: { icon: Building2, name: "Building/Company" },
  FileText: { icon: FileText, name: "File/Document" },
  Users: { icon: Users, name: "Users/Team" },
  Bell: { icon: Bell, name: "Notifications" },
  ClipboardCheck: { icon: ClipboardCheck, name: "Checklist" },
  TrendingUp: { icon: TrendingUp, name: "Analytics" },
  Clock: { icon: Clock, name: "Clock/Time" },
  Zap: { icon: Zap, name: "Quick/Lightning" },
  Plus: { icon: Plus, name: "Add/Plus" },
  Mail: { icon: Mail, name: "Email" },
  DollarSign: { icon: DollarSign, name: "Money/Finance" },
  Wallet: { icon: Wallet, name: "Wallet" },
  BarChart3: { icon: BarChart3, name: "Reports" },
  BookOpen: { icon: BookOpen, name: "Training" },
  Shield: { icon: Shield, name: "Safety/Benefits" },
  PiggyBank: { icon: PiggyBank, name: "Pensions" },
  LayoutDashboard: { icon: LayoutDashboard, name: "Dashboard" },
  Truck: { icon: Truck, name: "Fleet/Truck" },
  AlertCircle: { icon: AlertCircle, name: "Alert" },
};

export const AVAILABLE_DESTINATIONS = [
  { href: "/quotes/new", label: "New Quote", action: null },
  { href: "/quotes", label: "Quotes List", action: null },
  { href: "/clients", label: "Clients", action: "add-client" },
  { href: "/companies", label: "Companies", action: null },
  { href: "/employees", label: "Employees", action: null },
  { href: "/calendar", label: "Calendar", action: null },
  { href: "/invoices", label: "Invoices", action: "create-invoice" },
  { href: "/fleet", label: "Fleet Management", action: "add-vehicle" },
  { href: "/equipment", label: "Equipment", action: "add-equipment" },
  { href: "/safety-documents", label: "Health & Safety", action: null },
  { href: "/analytics", label: "Reporting / Analytics", action: null },
  { href: "/weekly-summary", label: "Weekly Summary", action: null },
  { href: "/templates", label: "Templates", action: null },
  { href: "/portal-settings", label: "Employee Portal", action: null },
  { href: "/email-templates", label: "Email Templates", action: null },
  { href: "/services", label: "Services & Goods", action: null },
  { href: "/accounts-receivable", label: "Accounts Receivable", action: null },
  { href: "/accounts-payable", label: "Accounts Payable", action: null },
  { href: "/training", label: "Training", action: null },
  { href: "/business-profile", label: "Business Profile", action: null },
  { href: "/documents", label: "Document Hub", action: null },
  { href: "/payroll", label: "Payroll", action: null },
  { href: "/tax-documents", label: "Tax Documents", action: null },
  { href: "/policies", label: "Policies & Procedures", action: null },
  { href: "/certifications", label: "Certifications", action: null },
  { href: "/audit-logs", label: "Audit Logs", action: null },
  { href: "/onboarding", label: "Onboarding", action: null },
  { href: "/performance", label: "Performance & Dev", action: null },
  { href: "/benefits", label: "Benefits", action: null },
  { href: "/banking-credit", label: "Banking & Credit", action: null },
  { href: "/union", label: "Unions", action: null },
];

export const BUTTON_COLORS = [
  { value: "bg-emerald-500 hover:bg-emerald-600", name: "Emerald", preview: "bg-emerald-500" },
  { value: "bg-cyan-500 hover:bg-cyan-600", name: "Cyan", preview: "bg-cyan-500" },
  { value: "bg-violet-500 hover:bg-violet-600", name: "Violet", preview: "bg-violet-500" },
  { value: "bg-orange-500 hover:bg-orange-600", name: "Orange", preview: "bg-orange-500" },
  { value: "bg-teal-500 hover:bg-teal-600", name: "Teal", preview: "bg-teal-500" },
  { value: "bg-pink-500 hover:bg-pink-600", name: "Pink", preview: "bg-pink-500" },
  { value: "bg-blue-500 hover:bg-blue-600", name: "Blue", preview: "bg-blue-500" },
  { value: "bg-red-500 hover:bg-red-600", name: "Red", preview: "bg-red-500" },
  { value: "bg-amber-500 hover:bg-amber-600", name: "Amber", preview: "bg-amber-500" },
  { value: "bg-indigo-500 hover:bg-indigo-600", name: "Indigo", preview: "bg-indigo-500" },
];

export const SUGGESTED_ACTIONS = [
  { label: "New Quote",        iconKey: "FileSignature", href: "/quotes/new",        action: null,              color: "bg-emerald-500 hover:bg-emerald-600" },
  { label: "Add Client",       iconKey: "UserPlus",      href: "/clients",           action: "add-client",      color: "bg-cyan-500 hover:bg-cyan-600" },
  { label: "Add Employee",     iconKey: "Users",         href: "/employees",         action: null,              color: "bg-violet-500 hover:bg-violet-600" },
  { label: "Create Invoice",   iconKey: "Receipt",       href: "/invoices",          action: "create-invoice",  color: "bg-teal-500 hover:bg-teal-600" },
  { label: "Add Payable",      iconKey: "DollarSign",    href: "/accounts-payable",  action: null,              color: "bg-orange-500 hover:bg-orange-600" },
  { label: "Schedule Job",     iconKey: "CalendarPlus",  href: "/calendar",          action: null,              color: "bg-pink-500 hover:bg-pink-600" },
  { label: "Open Calendar",    iconKey: "Calendar",      href: "/calendar",          action: null,              color: "bg-blue-500 hover:bg-blue-600" },
  { label: "Tax Overview",     iconKey: "FileText",      href: "/tax-documents",     action: null,              color: "bg-indigo-500 hover:bg-indigo-600" },
  { label: "Payroll",          iconKey: "DollarSign",    href: "/payroll",           action: null,              color: "bg-amber-500 hover:bg-amber-600" },
  { label: "Add Vehicle",      iconKey: "Car",           href: "/fleet",             action: "add-vehicle",     color: "bg-violet-500 hover:bg-violet-600" },
  { label: "Add Equipment",    iconKey: "Package",       href: "/equipment",         action: "add-equipment",   color: "bg-orange-500 hover:bg-orange-600" },
  { label: "Reports",          iconKey: "BarChart3",     href: "/analytics",         action: null,              color: "bg-red-500 hover:bg-red-600" },
];

// Phase 18.2: KPI tile catalogue. `metric` is the dotted path inside /api/dashboard/stats.
export const STAT_CARDS_CATALOG = {
  total_employees:    { id: "total_employees",    label: "Total Employees",    iconKey: "Users",          metric: "total_employees",     href: "/employees",                  description: null,                 roles: ["office_manager"] },
  total_companies:    { id: "total_companies",    label: "Companies",          iconKey: "Building2",      metric: "total_companies",     href: "/companies",                  description: null,                 roles: ["office_manager"] },
  pending_quotes:     { id: "pending_quotes",     label: "Pending Quotes",     iconKey: "Clock",          metric: "pending_quotes",      href: "/quotes",                     description: "Awaiting review",    roles: ["office_manager", "quoter"] },
  completed_jobs:     { id: "completed_jobs",     label: "Completed Jobs",     iconKey: "ClipboardCheck", metric: "completed_jobs",      href: "/quotes?status=completed",    description: null,                 roles: ["office_manager"] },
  my_quotes:          { id: "my_quotes",          label: "My Quotes",          iconKey: "FileText",       metric: "my_quotes",           href: "/quotes",                     description: null,                 roles: ["quoter"] },
  pending_requests:   { id: "pending_requests",   label: "Pending Requests",   iconKey: "Clock",          metric: "pending_requests",    href: "/quotes?status=requested",    description: "Need your attention",roles: ["quoter"] },
  approved:           { id: "approved",           label: "Approved",           iconKey: "ClipboardCheck", metric: "approved",            href: "/quotes?status=approved",     description: null,                 roles: ["quoter"] },
  unread_notifications:{ id: "unread_notifications", label: "Notifications",   iconKey: "Bell",           metric: "unread_notifications", href: null,                         description: null,                 roles: ["office_manager", "quoter", "employee"] },
  assigned_jobs:      { id: "assigned_jobs",      label: "Assigned Jobs",      iconKey: "Calendar",       metric: "assigned_jobs",       href: "/calendar",                   description: null,                 roles: ["employee"] },
  upcoming_jobs:      { id: "upcoming_jobs",      label: "Upcoming Jobs",      iconKey: "Clock",          metric: "upcoming_jobs",       href: "/calendar",                   description: null,                 roles: ["employee"] },
  // Phase 19.14: Finance Highlights — currency-formatted stat cards
  monthly_revenue:    { id: "monthly_revenue",    label: "Revenue This Month", iconKey: "TrendingUp",     metric: "monthly_revenue",     href: "/accounts-receivable",        description: "Paid this month",    roles: ["office_manager"], format: "currency" },
  outstanding_invoices:{ id: "outstanding_invoices", label: "Outstanding AR",  iconKey: "Wallet",         metric: "outstanding_invoices", href: "/accounts-receivable",       description: "Open balances",      roles: ["office_manager"], format: "currency" },
  overdue_invoices_amount:{ id: "overdue_invoices_amount", label: "Overdue $", iconKey: "AlertCircle",    metric: "overdue_invoices_amount", href: "/accounts-receivable",    description: "Past due",           roles: ["office_manager"], format: "currency" },
  ar_aged_60:         { id: "ar_aged_60",         label: "AR Aged 60+",        iconKey: "Clock",          metric: "ar_aged_60",          href: "/accounts-receivable",        description: "60–90 days overdue", roles: ["office_manager"], format: "currency" },
};

export const getDefaultStatCards = (isOfficeManager, isQuoter) => {
  if (isOfficeManager) return ["total_employees", "total_companies", "pending_quotes", "completed_jobs"];
  if (isQuoter)        return ["my_quotes", "pending_requests", "approved", "unread_notifications"];
  return ["assigned_jobs", "upcoming_jobs", "unread_notifications"];
};

// Phase 18.3: Smart Highlights packs
export const HIGHLIGHT_PACKS = {
  operations: {
    id: "operations",
    name: "Operations",
    description: "Visibility on the day-to-day flow: pending work + completed jobs + what's coming up.",
    iconKey: "Truck",
    cards: ["pending_quotes", "completed_jobs", "upcoming_jobs", "assigned_jobs"],
  },
  sales: {
    id: "sales",
    name: "Sales & Pipeline",
    description: "Quote pipeline health — pending, approved, and your own queue.",
    iconKey: "FileText",
    cards: ["pending_quotes", "approved", "my_quotes", "pending_requests"],
  },
  hr: {
    id: "hr",
    name: "HR",
    description: "Headcount and customer-base size at a glance.",
    iconKey: "Users",
    cards: ["total_employees", "total_companies"],
  },
  alerts: {
    id: "alerts",
    name: "Alerts & Inbox",
    description: "Anything that needs your attention right now.",
    iconKey: "Bell",
    cards: ["unread_notifications", "pending_quotes", "pending_requests"],
  },
  // Phase 19.14: Finance Highlights pack — revenue + AR aging at a glance.
  finance: {
    id: "finance",
    name: "Finance",
    description: "Cash flow snapshot: revenue this month, outstanding AR, overdue $, and aging buckets.",
    iconKey: "DollarSign",
    cards: ["monthly_revenue", "outstanding_invoices", "overdue_invoices_amount", "ar_aged_60"],
  },
};

// Default quick actions by role
export const getDefaultQuickActions = (isOfficeManager, isQuoter) => {
  if (isOfficeManager) {
    return [
      { id: "1", label: "New Quote",      iconKey: "FileSignature", href: "/quotes/new", action: null,             color: "bg-orange-500 hover:bg-orange-600" },
      { id: "2", label: "Schedule Job",   iconKey: "CalendarPlus",  href: "/calendar",   action: null,             color: "bg-pink-500 hover:bg-pink-600" },
      { id: "3", label: "Create Invoice", iconKey: "Receipt",       href: "/invoices",   action: "create-invoice", color: "bg-emerald-500 hover:bg-emerald-600" },
      { id: "4", label: "Add Client",     iconKey: "UserPlus",      href: "/clients",    action: "add-client",     color: "bg-cyan-500 hover:bg-cyan-600" },
      { id: "5", label: "Email",         iconKey: "Mail",          href: "/email",      action: null,             color: "bg-violet-500 hover:bg-violet-600" },
    ];
  } else if (isQuoter) {
    return [
      { id: "1", label: "New Quote", iconKey: "FileSignature", href: "/quotes/new", action: null, color: "bg-emerald-500 hover:bg-emerald-600" },
      { id: "2", label: "Add Client", iconKey: "UserPlus", href: "/clients", action: "add-client", color: "bg-cyan-500 hover:bg-cyan-600" },
      { id: "3", label: "View Calendar", iconKey: "Calendar", href: "/calendar", action: null, color: "bg-blue-500 hover:bg-blue-600" },
      { id: "4", label: "Companies", iconKey: "Building2", href: "/companies", action: null, color: "bg-violet-500 hover:bg-violet-600" },
    ];
  } else {
    return [
      { id: "1", label: "My Schedule", iconKey: "Calendar", href: "/calendar", action: null, color: "bg-emerald-500 hover:bg-emerald-600" },
      { id: "2", label: "Safety Docs", iconKey: "FileText", href: "/safety-documents", action: null, color: "bg-blue-500 hover:bg-blue-600" },
    ];
  }
};

// Phase 18: Smart Starter Packs
export const STARTER_PACKS = {
  general: {
    id: "general",
    name: "General (Default)",
    description: "A balanced mix for everyday business management.",
    iconKey: "LayoutDashboard",
    actions: [
      { id: "g1", label: "New Quote",      iconKey: "FileSignature", href: "/quotes/new", action: null,             color: "bg-orange-500 hover:bg-orange-600" },
      { id: "g2", label: "Schedule Job",   iconKey: "CalendarPlus",  href: "/calendar",   action: null,             color: "bg-pink-500 hover:bg-pink-600" },
      { id: "g3", label: "Create Invoice", iconKey: "Receipt",       href: "/invoices",   action: "create-invoice", color: "bg-emerald-500 hover:bg-emerald-600" },
      { id: "g4", label: "Add Client",     iconKey: "UserPlus",      href: "/clients",    action: "add-client",     color: "bg-cyan-500 hover:bg-cyan-600" },
      { id: "g5", label: "Email",         iconKey: "Mail",          href: "/email",      action: null,             color: "bg-violet-500 hover:bg-violet-600" },
    ],
  },
  finance: {
    id: "finance",
    name: "Finance Focus",
    description: "AR, AP, invoicing, payroll, and tax — all one click away.",
    iconKey: "DollarSign",
    actions: [
      { id: "f1", label: "Create Invoice", iconKey: "Receipt", href: "/invoices", action: "create-invoice", color: "bg-teal-500 hover:bg-teal-600" },
      { id: "f2", label: "Receivables", iconKey: "Wallet", href: "/accounts-receivable", action: null, color: "bg-emerald-500 hover:bg-emerald-600" },
      { id: "f3", label: "Payables", iconKey: "DollarSign", href: "/accounts-payable", action: null, color: "bg-orange-500 hover:bg-orange-600" },
      { id: "f4", label: "Payroll", iconKey: "DollarSign", href: "/payroll", action: null, color: "bg-amber-500 hover:bg-amber-600" },
      { id: "f5", label: "Tax Docs", iconKey: "FileText", href: "/tax-documents", action: null, color: "bg-indigo-500 hover:bg-indigo-600" },
      { id: "f6", label: "Reports", iconKey: "BarChart3", href: "/analytics", action: null, color: "bg-red-500 hover:bg-red-600" },
    ],
  },
  operations: {
    id: "operations",
    name: "Operations Focus",
    description: "Quotes, scheduling, fleet, and equipment for daily ops.",
    iconKey: "Truck",
    actions: [
      { id: "o1", label: "New Quote", iconKey: "FileSignature", href: "/quotes/new", action: null, color: "bg-emerald-500 hover:bg-emerald-600" },
      { id: "o2", label: "Schedule Job", iconKey: "CalendarPlus", href: "/calendar", action: null, color: "bg-pink-500 hover:bg-pink-600" },
      { id: "o3", label: "Add Vehicle", iconKey: "Car", href: "/fleet", action: "add-vehicle", color: "bg-violet-500 hover:bg-violet-600" },
      { id: "o4", label: "Add Equipment", iconKey: "Package", href: "/equipment", action: "add-equipment", color: "bg-orange-500 hover:bg-orange-600" },
      { id: "o5", label: "Clients", iconKey: "Users", href: "/clients", action: null, color: "bg-cyan-500 hover:bg-cyan-600" },
      { id: "o6", label: "Safety Docs", iconKey: "FileText", href: "/safety-documents", action: null, color: "bg-blue-500 hover:bg-blue-600" },
    ],
  },
  hr: {
    id: "hr",
    name: "HR Focus",
    description: "Employees, training, performance, benefits, and ROEs.",
    iconKey: "Users",
    actions: [
      { id: "h1", label: "Employees", iconKey: "Users", href: "/employees", action: null, color: "bg-violet-500 hover:bg-violet-600" },
      { id: "h2", label: "Training", iconKey: "BookOpen", href: "/training", action: null, color: "bg-emerald-500 hover:bg-emerald-600" },
      { id: "h3", label: "Performance", iconKey: "TrendingUp", href: "/performance", action: null, color: "bg-cyan-500 hover:bg-cyan-600" },
      { id: "h4", label: "Benefits", iconKey: "Shield", href: "/benefits", action: null, color: "bg-teal-500 hover:bg-teal-600" },
      { id: "h5", label: "Pensions", iconKey: "PiggyBank", href: "/pensions", action: null, color: "bg-amber-500 hover:bg-amber-600" },
      { id: "h6", label: "ROEs", iconKey: "FileText", href: "/roes", action: null, color: "bg-blue-500 hover:bg-blue-600" },
    ],
  },
};
